'use client'

import { useState } from 'react'
import { Control, useController } from 'react-hook-form'

import { gt } from '~/lib/gt'

import styles from './Checkbox.module.css'

type SharedProps = {
  label: React.ReactNode
  labelAddition?: string
  indeterminate?: boolean
  disabled?: boolean
  showMore?: React.ReactNode
  error?: boolean
  className?: string
  expandBtn?: {
    true: string
    false: string
  }
}

export type CheckboxProps =
  | (SharedProps & { control: Control<any>; name: string; checked?: never; onChange?: never })
  | (SharedProps & { control?: never; name?: never; checked: boolean; onChange: (checked: boolean) => void })

const CheckboxBase: React.FC<SharedProps & { checked: boolean; onChange: (checked: boolean) => void }> = ({
  label,
  labelAddition,
  disabled,
  showMore,
  indeterminate: defaultIndeterminate,
  error = false,
  className,
  expandBtn = {
    true: gt.tp('Common', 'Hide'),
    false: gt.tp('Common', 'Expand'),
  },
  checked,
  onChange,
}) => {
  const [expand, setExpand] = useState(false)
  const [isIndeterminate, setIsIndeterminate] = useState(defaultIndeterminate)
  const icon = isIndeterminate ? 'indeterminate' : 'check-bold'

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isIndeterminate) setIsIndeterminate(false)
    onChange(event.target.checked)
  }

  return (
    <label className={`${styles.checkboxField} ${disabled ? styles.disabled : ''} ${error ? styles.error : ''} ${className ?? ''}`}>
      <div className={`${styles.checkboxBox} ${checked ? styles.checked : ''} ${disabled ? styles.disabled : ''}`}>
        {(checked || isIndeterminate) && <span className={`${styles.checkboxIcon} ${styles[icon]}`}>✔️</span>}
      </div>
      <div className={styles.label}>
        {label}
        {labelAddition && <span className={styles.labelAddition}>&nbsp;{labelAddition}</span>}
        {showMore && (
          <>
            {expand && <span className={styles.labelExpanded}> {showMore}</span>}
            <span
              className={styles.showMore}
              onClick={event => {
                event.stopPropagation()
                event.preventDefault()
                setExpand(!expand)
              }}>
              {' '}
              {expandBtn[String(expand) as 'true' | 'false']}
            </span>
          </>
        )}
      </div>
      <input
        type='checkbox'
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-invalid={error || undefined}
        className={styles.hiddenInput}
      />
    </label>
  )
}

const CheckboxWithControl: React.FC<SharedProps & { control: Control<any>; name: string }> = ({ control, name, ...rest }) => {
  const {
    field: { value: checked, onChange },
  } = useController({ name, control, defaultValue: false })

  return <CheckboxBase checked={!!checked} onChange={val => onChange(val)} {...rest} />
}

const CheckboxView: React.FC<CheckboxProps> = props =>
  props.control !== undefined ? (
    <CheckboxWithControl {...(props as SharedProps & { control: Control<any>; name: string })} />
  ) : (
    <CheckboxBase {...(props as SharedProps & { checked: boolean; onChange: (checked: boolean) => void })} />
  )

export default CheckboxView
